import { useState } from "react";
import { Activity, ArrowRight, Globe, Loader2 } from "lucide-react";
import { useToast } from "../ToastContext";
import {
  injectTeamScenarioEntry,
  injectTeamScenarioStage,
} from "../../api/services/platformAdminService";
import InjectResultsModal from "./InjectResultsModal";
import EntryInjectGroups, {
  entryKey,
  firstEntryOf,
  runHasEntries,
} from "./EntryInjectGroups";
import { applyAdvancedResults } from "./injectOutcome";

// Status of a stage across every team on the scenario.
const STAGE_STATUS = {
  injected: {
    label: "injected",
    badge: "border-rose-500/40 bg-rose-500/10 text-rose-400",
  },
  partial: {
    label: "partial",
    badge: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  },
  ready: {
    label: "ready",
    badge: "border-(--color-border) text-(--color-text-muted)",
  },
};

function TabButton({ label, dotClass, isActive, activeClass, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
        isActive
          ? activeClass
          : "border-(--color-border) text-(--color-text-muted) hover:bg-white/3"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
      {label}
    </button>
  );
}

function getStageStatus(stage, runs) {
  const atOrPast = runs.filter((r) => (r.current_stage || 0) >= stage).length;
  if (atOrPast === runs.length) return "injected";
  if (atOrPast === 0) return "ready";
  return "partial";
}

function StageRow({ stageInfo, status, isSelected, onSelect }) {
  const meta = STAGE_STATUS[status];
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
        isSelected
          ? "border-orange-500/60 bg-orange-500/5"
          : "border-(--color-border) hover:bg-white/3"
      }`}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs font-semibold ${
          isSelected
            ? "border-orange-500/60 text-orange-400"
            : "border-(--color-border) text-(--color-text-muted)"
        }`}
      >
        {stageInfo.stage}
      </span>
      <span className="min-w-0 flex-1 text-xs leading-relaxed">
        <span className="block font-semibold text-(--color-text-primary)">
          {stageInfo.name || `Stage ${stageInfo.stage}`}
        </span>
        {stageInfo.description ? (
          // Clamp unselected rows so the list stays scannable; the selected
          // stage shows its full description.
          <span
            className={`mt-0.5 text-(--color-text-muted) ${
              isSelected ? "block" : "line-clamp-3"
            }`}
          >
            {stageInfo.description}
          </span>
        ) : null}
      </span>
      <span
        className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-semibold ${meta.badge}`}
      >
        {meta.label}
      </span>
    </button>
  );
}

// Mirrors the panel shell so the detail page doesn't jump when data lands.
export function GlobalInjectPanelSkeleton() {
  return (
    <aside className="flex flex-col rounded-lg border border-(--color-border) qv-card qv-noise overflow-hidden max-h-144">
      <div className="px-5 py-4 border-b border-(--color-border) space-y-2">
        <div className="qv-skeleton h-5 w-32 rounded" />
        <div className="qv-skeleton h-3 w-56 rounded" />
      </div>
      <div className="flex-1 p-4 space-y-3">
        <div className="qv-skeleton h-16 w-full rounded-lg" />
        <div className="qv-skeleton h-14 w-full rounded-lg" />
        <div className="qv-skeleton h-14 w-full rounded-lg" />
        <div className="qv-skeleton h-14 w-full rounded-lg" />
      </div>
      <div className="border-t border-(--color-border) p-3">
        <div className="qv-skeleton h-11 w-full rounded-lg" />
      </div>
    </aside>
  );
}

export default function GlobalInjectPanel({ runs, scenarioId, onStageAdvanced }) {
  const { showToast } = useToast();
  const [isInjecting, setIsInjecting] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [activeTab, setActiveTab] = useState("attack");

  // Stage catalogue is the same for every team; take it from the first run.
  const stageList = runs[0]?.stages || [];
  const totalStages = runs[0]?.total_stages || stageList.length;

  // Entries are the same for every team on the scenario; read from the first.
  const entriesRun = runs[0];
  const hasEntries = runHasEntries(entriesRun);
  const [selectedEntry, setSelectedEntry] = useState(() =>
    firstEntryOf(entriesRun),
  );
  const selectedEntryKey = selectedEntry
    ? entryKey(selectedEntry.family, selectedEntry.name)
    : null;

  // firingKey is "<family>:<name>" while a fire call is in flight, else null.
  const [firingKey, setFiringKey] = useState(null);
  const fireEntry = async (family, name) => {
    if (firingKey) return;
    setFiringKey(`${family}:${name}`);
    try {
      // no teamId → broadcast to every live run
      const data = await injectTeamScenarioEntry({ scenarioId, family, name });
      setResultsData(data);
      if (data?.fired > 0) {
        showToast(
          `"${data.name}" fired for ${data.fired} team${data.fired === 1 ? "" : "s"}.`,
          "success",
        );
      }
    } catch (err) {
      showToast(err?.message || "Failed to send global inject.", "error");
    } finally {
      setFiringKey(null);
    }
  };

  // Default to the stage the furthest-behind team needs next.
  const minStage = Math.min(...runs.map((r) => r.current_stage || 0));
  const defaultStage = Math.min(minStage + 1, totalStages);
  const [selectedStage, setSelectedStage] = useState(defaultStage);

  const atOrPast = runs.filter(
    (r) => (r.current_stage || 0) >= selectedStage,
  ).length;

  const handleInject = async () => {
    setIsInjecting(true);
    try {
      const data = await injectTeamScenarioStage({
        scenarioId,
        stage: selectedStage,
        teamId: "", // empty = broadcast to every live run
      });
      setResultsData(data);
      applyAdvancedResults(data?.results, onStageAdvanced, selectedStage);
      if (data?.advanced > 0) {
        showToast(
          `Stage ${data.stage} injected for ${data.advanced} team${data.advanced === 1 ? "" : "s"}.`,
          "success",
        );
      }
    } catch (err) {
      showToast(err?.message || "Failed to send global inject.", "error");
    } finally {
      setIsInjecting(false);
    }
  };

  return (
    <aside className="flex flex-col rounded-lg border border-(--color-border) qv-card qv-noise overflow-hidden max-h-144">
      <div className="px-5 py-4 border-b border-(--color-border) shrink-0">
        <h2 className="text-base font-semibold text-(--color-text-primary)">
          Global inject
        </h2>
        <p className="mt-0.5 text-xs text-(--color-text-muted)">
          {activeTab === "noise"
            ? "Fire noise, decoy, or department traffic at every team."
            : "Push an attack stage to every team at once."}
        </p>
        {hasEntries && (
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <TabButton
              label="Attack"
              dotClass="bg-orange-400"
              isActive={activeTab === "attack"}
              activeClass="border-orange-500/60 bg-orange-500/10 text-orange-400"
              onClick={() => setActiveTab("attack")}
            />
            <TabButton
              label="Noise"
              dotClass="bg-sky-400"
              isActive={activeTab === "noise"}
              activeClass="border-sky-500/60 bg-sky-500/10 text-sky-400"
              onClick={() => setActiveTab("noise")}
            />
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
        <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-emerald-500/40 bg-emerald-500/10">
            <Globe className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </span>
          <div>
            <p className="text-xs font-semibold text-(--color-text-primary)">
              All teams
            </p>
            <p className="text-[11px] leading-relaxed text-(--color-text-muted)">
              A global inject reaches every team at once. To hit one team, use
              its card.
            </p>
          </div>
        </div>

        {activeTab === "noise" ? (
          <EntryInjectGroups
            run={entriesRun}
            selectedKey={selectedEntryKey}
            onSelect={(family, name) => setSelectedEntry({ family, name })}
          />
        ) : (
          <div className="space-y-2">
            {stageList.map((stageInfo) => (
              <StageRow
                key={stageInfo.stage}
                stageInfo={stageInfo}
                status={getStageStatus(stageInfo.stage, runs)}
                isSelected={stageInfo.stage === selectedStage}
                onSelect={() => setSelectedStage(stageInfo.stage)}
              />
            ))}
          </div>
        )}
      </div>

      {activeTab === "attack" ? (
        <div className="border-t border-(--color-border) p-3 shrink-0">
          <button
            type="button"
            onClick={handleInject}
            disabled={isInjecting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isInjecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            {isInjecting
              ? "Injecting…"
              : `Inject Stage ${selectedStage} → all teams`}
          </button>
          <p className="mt-2 text-center text-[11px] text-(--color-text-muted)">
            {atOrPast} of {runs.length} already at or past this stage.
          </p>
        </div>
      ) : (
        <div className="border-t border-(--color-border) p-3 shrink-0">
          <button
            type="button"
            onClick={() =>
              selectedEntry &&
              fireEntry(selectedEntry.family, selectedEntry.name)
            }
            disabled={!selectedEntry || Boolean(firingKey)}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-linear-to-b from-sky-300 to-sky-400 px-4 py-3.5 text-sm font-bold text-sky-950 shadow-lg shadow-sky-500/30 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
          >
            {firingKey ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Activity className="h-4 w-4 shrink-0" />
            )}
            {firingKey ? "Injecting…" : "Inject Noise → all teams"}
          </button>
        </div>
      )}

      {resultsData && (
        <InjectResultsModal
          data={resultsData}
          onClose={() => setResultsData(null)}
        />
      )}
    </aside>
  );
}
